import { MRT_ColumnDef, MRT_DefaultColumn, MRT_RowData } from "material-react-table";
import { EmptyRowsFallback } from "../EmptyRowsFallback";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { IconButton, useTheme } from "@mui/material";
import { useMemo, useState } from "react";
import { GridColumnsType } from "src/components/Grid/hooks/columnsExtension/useGridColumns";
import { useTranslation } from "react-i18next";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import ActionsPopover from "src/components/ActionsPopover/ActionsPopover";
import { ExtendedGridPagination } from "../hooks/gridUrlSync/useGridPaginationUrlSync";
import { GridSort } from "../hooks/gridUrlSync/useGridSortUrlSync";
import { GridFilters } from "../hooks/gridUrlSync/useGridFiltersUrlSync";
import { Button, FoodsIconButton, MRT_GRID_HUB_LAYOUT, type MRTGridAppearance } from "@foodpilot/foods";
import { MenuItemType } from "src/components/ActionsPopover/ActionsPopover.types";
import { GridHeaderWithFilters } from "../headers/GridHeaderWithFilters";
import type { TableAction } from "../headers/GridTableActions";
import { useColumnsTableAction } from "../headers/useColumnsTableAction";
import { resolveEntityListColumns } from "./entityListColumnLayout";
import { AppMRTGrid } from "../AppMRTGrid";

type PrimaryGridProps<Data extends MRT_RowData> = {
    readonly gridKey: string;
    columns: MRT_ColumnDef<Data, any>[];
    data: Data[] | undefined;
    menuItems?: MenuItemType<Data>[];
    isLoading?: boolean;
    rowActionIcon?: React.ReactNode;

    filters: GridFilters;
    sort: GridSort;
    pagination: ExtendedGridPagination;
    columnConfig: GridColumnsType<Data>;
    enableViews?: boolean;
    enableDynamicRowHeight?: boolean;
    emptyMessage?: string;
    renderEmptyState?: (actions: { resetFilters: () => void }) => React.ReactNode;
    onRowClick?: (row: Data) => void;
    enableServerSidePagination?: boolean;
    enableServerSideSorting?: boolean;
    enableServerSideFiltering?: boolean;
    enablePagination?: boolean;
    enableRowActions?: boolean;
    rowHeight?: number;
    actionHeader?: string;
    mobileTableMode?: "compact" | "scroll";
    enableColumnResizing?: boolean;
    appearance?: MRTGridAppearance;
    getRowActionLabel?: (row: Data) => string;
    tableAriaLabel?: string;
    tableActions?: TableAction[];
    enableColumnsAction?: boolean;
    viewTabsLeading?: React.ReactNode;
    tablePanelProps?: Pick<React.HTMLAttributes<HTMLDivElement>, "id" | "role" | "aria-labelledby">;
};

const hasSummaryFooter = <Data extends MRT_RowData>(columns: MRT_ColumnDef<Data, any>[]): boolean =>
    columns.some(
        (column) =>
            column.Footer !== undefined ||
            (column.columns !== undefined && hasSummaryFooter(column.columns as MRT_ColumnDef<Data, any>[])),
    );

const getColumnId = <Data extends MRT_RowData>(column: MRT_ColumnDef<Data, any>): string => {
    if (column.id !== undefined) return String(column.id);

    if (column.accessorKey !== undefined) return String(column.accessorKey).replaceAll(".", "_");

    return typeof column.header === "string" ? column.header : "";
};

const getLeafColumns = <Data extends MRT_RowData>(columns: MRT_ColumnDef<Data, any>[]): MRT_ColumnDef<Data, any>[] =>
    columns.flatMap((column) =>
        column.columns === undefined ? [column] : getLeafColumns(column.columns as MRT_ColumnDef<Data, any>[]),
    );

export const PrimaryGrid = <Data extends MRT_RowData>(props: PrimaryGridProps<Data>) => {
    const {
        columns,
        columnConfig,
        data,
        filters,
        gridKey,
        isLoading = false,
        menuItems = [],
        pagination,
        sort,
        enableViews = false,
        rowActionIcon,
        enableDynamicRowHeight = false,
        emptyMessage,
        renderEmptyState,
        onRowClick,
        enableRowActions = true,
        rowHeight,
        actionHeader = "",
        mobileTableMode = "compact",
        enableColumnResizing = true,
        enableServerSideFiltering = true,
        enableServerSidePagination = true,
        enableServerSideSorting = true,
        enablePagination = true,
        appearance,
        getRowActionLabel,
        tableAriaLabel,
        tableActions = [],
        enableColumnsAction = false,
        viewTabsLeading,
        tablePanelProps,
    } = props;

    const [rowMenuAnchor, setRowMenuAnchor] = useState<null | HTMLElement>(null);
    const [selectedRowItem, setSelectedRowItem] = useState<Data | null>(null);

    const { t } = useTranslation();
    const theme = useTheme();
    const { isMobile } = useResponsiveBreakpoints();
    const usesHubAppearance = appearance === "hub";
    const columnsAction = useColumnsTableAction(columnConfig);
    const headerActions =
        enableColumnsAction ? [{ ...columnsAction, shouldShow: () => !isMobile }, ...tableActions] : tableActions;
    const allowsColumnSizing = enableColumnResizing && !usesHubAppearance;
    const usesServerSideFiltering = usesHubAppearance ? enableServerSideFiltering : true;
    const usesServerSidePagination = usesHubAppearance ? enableServerSidePagination : true;
    const usesServerSideSorting = usesHubAppearance ? enableServerSideSorting : true;
    const resolvedColumns = useMemo(
        () => resolveEntityListColumns(columns, usesHubAppearance ? "hub" : "legacy"),
        [columns, usesHubAppearance],
    );
    const usesScrollableMobileTable = isMobile && mobileTableMode === "scroll";
    const rowActionsLabel = actionHeader || t("common.actions", "Actions");
    const resolvedActionColumnWidth =
        usesHubAppearance ? MRT_GRID_HUB_LAYOUT.actionColumnWidth
        : isMobile ? 35
        : 80;
    const leafColumns = useMemo(() => getLeafColumns(resolvedColumns), [resolvedColumns]);
    const mobileScrollableTableMinWidth = leafColumns.reduce(
        (totalWidth, column) => {
            const columnId = getColumnId(column);
            if (columnId !== "" && columnConfig.columnVisibility[columnId] === false) return totalWidth;

            const persistedSize =
                !allowsColumnSizing || columnId === "" ? undefined : columnConfig.columnSizing[columnId];
            return totalWidth + (persistedSize ?? column.size ?? MRT_DefaultColumn.size);
        },
        enableRowActions ? resolvedActionColumnWidth : 0,
    );

    const totalPages = Math.ceil((pagination.totalItems ?? 0) / pagination.pagination.pageSize);
    const shouldEnablePagination = usesHubAppearance ? enablePagination && totalPages > 0 : true;
    const displaysSummaryFooter = hasSummaryFooter(resolvedColumns);
    const rowHeightStyle =
        usesHubAppearance ? undefined
        : enableDynamicRowHeight ? "auto"
        : rowHeight !== undefined ? `${rowHeight}px`
        : "52px";

    const leafColumnIds = [...new Set(leafColumns.map(getColumnId).filter((columnId) => columnId !== ""))];
    // Compact mobile always exposes the business identity column declared by
    // the screen. Persisted desktop order/visibility must not turn a product,
    // ingredient or supplier row into an unrelated score/reference-only row.
    const defaultMobileColumnId = leafColumnIds[0];
    const visibleColumnIdsOnMobile =
        usesHubAppearance ?
            defaultMobileColumnId === undefined ?
                []
            :   [defaultMobileColumnId]
        :   ["title"];
    const mobileColumnVisibility = Object.fromEntries(
        leafColumnIds.map((columnId) => [columnId, visibleColumnIdsOnMobile.includes(columnId)]),
    );
    const compactMobileDataColumnWidth = enableRowActions ? `calc(100% - ${resolvedActionColumnWidth}px)` : "100%";

    const menuItemsWithRowHandler =
        menuItems.map((menuItem) => {
            return {
                ...menuItem,
                onClick: () => {
                    if (selectedRowItem === null) return;

                    menuItem.onClick(selectedRowItem);
                    setRowMenuAnchor(null);
                },
                shouldShow: () => {
                    // Hide button anyway if my selection is null;
                    // It means the Menu Item shouldn't be visible anyway;
                    if (selectedRowItem === null) return false;
                    const _shouldShow = menuItem.shouldShow?.(selectedRowItem);

                    const showIfRowSelectByDefault = true;
                    return _shouldShow ?? showIfRowSelectByDefault;
                },
            };
        }) ?? [];

    const _rowsPerPage = [5, 10, 15, 20, 25, 30, 50, 100];
    const doesItemsExistInRows = _rowsPerPage.find((i) => i === pagination.pagination.pageSize);
    const rowsPerPage =
        doesItemsExistInRows !== undefined ? _rowsPerPage : (
            [..._rowsPerPage, pagination.pagination.pageSize].sort((a, b) => {
                return a - b;
            })
        );

    return (
        <>
            <ActionsPopover
                appearance={usesHubAppearance ? "themed" : "legacy"}
                menuItems={menuItemsWithRowHandler}
                anchor={rowMenuAnchor}
                closeWindow={() => setRowMenuAnchor(null)}
            />
            {enableViews === true && (
                <GridHeaderWithFilters
                    gridFilters={filters}
                    columns={resolvedColumns}
                    gridKey={gridKey}
                    columnsConfig={columnConfig}
                    tableActions={headerActions}
                    tableActionsAppearance={usesHubAppearance ? "themed" : "legacy"}
                    viewTabsLeading={viewTabsLeading}
                />
            )}
            <div
                {...tablePanelProps}
                style={{
                    backgroundColor: theme.palette.background.paper,
                }}
            >
                <AppMRTGrid
                    appearance={appearance}
                    columns={resolvedColumns}
                    data={data ?? []}
                    globalFilterFn="contains"
                    enableRowActions={enableRowActions}
                    enableRowSelection={false}
                    enablePagination={shouldEnablePagination}
                    enableSelectAll={false}
                    enableColumnPinning={!isMobile || usesScrollableMobileTable}
                    enableStickyFooter={usesHubAppearance ? displaysSummaryFooter : true}
                    enableTableFooter={usesHubAppearance ? displaysSummaryFooter : undefined}
                    enableHiding={!isMobile} // Désactiver masquage colonnes en mobile
                    enableColumnResizing={allowsColumnSizing && !isMobile}
                    muiTableBodyRowProps={
                        onRowClick ?
                            (props) => ({
                                onClick: (event: React.MouseEvent) => {
                                    try {
                                        const target = event.target as HTMLElement;
                                        const isActionElement = target.closest(
                                            'button, a, [role="button"], .MuiIconButton-root, .MuiButton-root, [data-action]',
                                        );

                                        if (isActionElement) {
                                            return;
                                        }

                                        const rowData = props?.row?.original || props?.row || props;
                                        if (rowData && typeof rowData === "object") {
                                            onRowClick(rowData);
                                        }
                                    } catch (error) {
                                        console.error("Error in row click handler:", error);
                                    }
                                },
                                sx: {
                                    ...(rowHeightStyle !== undefined && { height: rowHeightStyle }),
                                    cursor: "pointer",
                                },
                            })
                        : rowHeightStyle !== undefined ?
                            {
                                sx: {
                                    height: rowHeightStyle,
                                },
                            }
                        :   undefined
                    }
                    displayColumnDefOptions={{
                        "mrt-row-actions": {
                            size: resolvedActionColumnWidth,
                            grow: false,
                            ...(usesHubAppearance && {
                                muiTableBodyCellProps: {
                                    align: "center",
                                    sx: { padding: 1 },
                                },
                            }),
                        },
                    }}
                    muiTableContainerProps={{
                        sx: {
                            ...(usesScrollableMobileTable && {
                                overflowX: "auto",
                                maxWidth: "100%",
                            }),
                            ...(isMobile &&
                                !usesScrollableMobileTable && {
                                    overflow: "hidden",
                                    maxWidth: "100%",
                                }),
                        },
                    }}
                    muiTableProps={{
                        ...(tableAriaLabel !== undefined && { "aria-label": tableAriaLabel }),
                        ...(usesScrollableMobileTable && {
                            sx: {
                                minWidth: `${mobileScrollableTableMinWidth}px`,
                            },
                        }),
                        ...(isMobile &&
                            !usesScrollableMobileTable && {
                                sx: {
                                    tableLayout: "fixed",
                                    width: "100%",
                                    ...(!usesHubAppearance && {
                                        "& .MuiTableCell-root": {
                                            padding: "12px 8px",
                                            fontSize: "0.8rem",
                                        },
                                        "& .MuiTableHead-root .MuiTableCell-root": {
                                            fontSize: "1rem",
                                            fontWeight: 600,
                                        },
                                    }),
                                    "& .MuiTableCell-root:first-of-type": {
                                        width: compactMobileDataColumnWidth,
                                        maxWidth: compactMobileDataColumnWidth,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    },
                                    "& .MuiTableHead-root .MuiTableCell-root:first-of-type": {
                                        width: compactMobileDataColumnWidth,
                                        maxWidth: compactMobileDataColumnWidth,
                                        overflow: "hidden",
                                    },
                                    ...(enableRowActions && {
                                        "& .MuiTableCell-root:last-child": {
                                            width: `${resolvedActionColumnWidth}px`,
                                            maxWidth: `${resolvedActionColumnWidth}px`,
                                            position: "sticky",
                                            right: 0,
                                            backgroundColor: theme.palette.background.paper,
                                            zIndex: 1,
                                            ...(!usesHubAppearance && { padding: "12px 4px" }),
                                        },
                                        "& .MuiTableHead-root .MuiTableCell-root:last-child": {
                                            width: `${resolvedActionColumnWidth}px`,
                                            maxWidth: `${resolvedActionColumnWidth}px`,
                                            position: "sticky",
                                            right: 0,
                                            backgroundColor: theme.palette.background.paper,
                                            zIndex: 2,
                                            ...(!usesHubAppearance && { padding: "12px 4px" }),
                                        },
                                    }),
                                },
                            }),
                    }}
                    muiPaginationProps={
                        usesHubAppearance ? undefined : (
                            {
                                rowsPerPageOptions: rowsPerPage,
                                sx: {
                                    padding: isMobile ? "0" : undefined, // Pas de padding supplémentaire
                                    margin: isMobile ? "0" : undefined, // Pas de margin supplémentaire
                                    display: "flex",
                                    justifyContent: "center", // Centrer la pagination
                                    overflow: "hidden", // Empêcher le débordement
                                    "& .MuiTablePagination-toolbar": {
                                        paddingLeft: isMobile ? "2px" : undefined,
                                        paddingRight: isMobile ? "2px" : undefined,
                                        justifyContent: "center", // Centrer le contenu
                                        minHeight: isMobile ? "40px" : undefined, // Hauteur plus compacte
                                        flexWrap: "nowrap", // Forcer sur une seule ligne
                                        gap: isMobile ? "1px" : undefined, // Espacement minimal
                                    },
                                    "& .MuiTablePagination-selectLabel": {
                                        fontSize: isMobile ? "0.65rem" : undefined, // Plus petit
                                        margin: isMobile ? "0 1px" : undefined,
                                        whiteSpace: "nowrap", // Pas de retour à la ligne
                                    },
                                    "& .MuiTablePagination-displayedRows": {
                                        fontSize: isMobile ? "0.65rem" : undefined, // Plus petit
                                        margin: isMobile ? "0 1px" : undefined,
                                        whiteSpace: "nowrap", // Pas de retour à la ligne
                                    },
                                    "& .MuiTablePagination-select": {
                                        fontSize: isMobile ? "0.65rem" : undefined, // Plus petit
                                        minWidth: isMobile ? "30px" : undefined, // Plus compact
                                    },
                                    "& .MuiInputBase-root": {
                                        fontSize: isMobile ? "0.65rem" : undefined, // Plus petit
                                    },
                                    "& .MuiIconButton-root": {
                                        padding: isMobile ? "2px" : undefined, // Très compact
                                        minWidth: isMobile ? "28px" : undefined, // Plus petit
                                        width: isMobile ? "28px" : undefined,
                                        height: isMobile ? "28px" : undefined,
                                    },
                                    "& .MuiTablePagination-actions": {
                                        marginLeft: isMobile ? "2px" : undefined, // Espacement minimal
                                    },
                                },
                            }
                        )
                    }
                    renderRowActions={({ row }) => {
                        const accessibleLabel = getRowActionLabel?.(row.original) ?? rowActionsLabel;
                        const actionButtonSx = {
                            ...(usesHubAppearance ? {} : { transform: `translateX(${theme.spacing(-0.5)})` }),
                        };
                        const openRowMenu = (event: React.MouseEvent<HTMLElement>) => {
                            setSelectedRowItem(row.original);
                            setRowMenuAnchor(event.currentTarget);
                        };

                        const buttonProps = {
                            "aria-label": accessibleLabel,
                            "aria-haspopup": "menu" as const,
                            "aria-expanded":
                                rowMenuAnchor !== null && selectedRowItem === row.original ? true : undefined,
                            onClick: openRowMenu,
                            sx: actionButtonSx,
                        };

                        if (!usesHubAppearance) {
                            return <IconButton {...buttonProps}>{rowActionIcon ?? <MoreHorizIcon />}</IconButton>;
                        }

                        return rowActionIcon === undefined ?
                                <FoodsIconButton icon="actions" size="s" buttonProps={buttonProps} />
                            :   <Button variant="icon-s-nobackground" {...buttonProps}>
                                    {rowActionIcon}
                                </Button>;
                    }}
                    renderEmptyRowsFallback={() =>
                        renderEmptyState?.({ resetFilters: () => filters.setColumnFilters([]) }) ?? (
                            <EmptyRowsFallback
                                message={emptyMessage || t("il_n_y_a_aucun_produit_a_afficher")}
                                appearance={usesHubAppearance ? "product-v2" : "legacy"}
                            />
                        )
                    }
                    state={{
                        ...(usesHubAppearance ? { showSkeletons: isLoading } : { isLoading }),
                        // We will want to implement a global filter on this Grid aswell
                        // globalFilter: delayedSearchProduct,
                        columnVisibility:
                            isMobile && !usesScrollableMobileTable ? mobileColumnVisibility : (
                                columnConfig.columnVisibility
                            ),
                        columnOrder: columnConfig.columnOrder,
                        pagination: pagination.pagination,
                        columnPinning:
                            isMobile && !usesScrollableMobileTable ?
                                usesHubAppearance ?
                                    {
                                        left: [],
                                        right: enableRowActions ? ["mrt-row-actions"] : [],
                                    }
                                :   {}
                            :   {
                                    left: isMobile ? [] : [columnConfig.columnOrder[0]],
                                    right: enableRowActions ? ["mrt-row-actions"] : [],
                                },
                        columnSizing: allowsColumnSizing ? columnConfig.columnSizing : {},
                        sorting: sort.sorting,
                        columnFilters: filters.columnFilters,
                    }}
                    // enableGrouping={true}
                    onColumnSizingChange={allowsColumnSizing ? columnConfig.setColumnSizing : undefined}
                    // Server-side pagination
                    rowCount={usesServerSidePagination ? pagination.totalItems : undefined}
                    manualPagination={usesServerSidePagination}
                    onPaginationChange={pagination.setPagination}
                    localization={{
                        actions: actionHeader,
                        filterByColumn: t("component.table.filter_by", "Filter by {column}"),
                        filteringByColumn: t("component.table.filtered_by", "Filtering by {column} - {filterValue}"),
                        rowsPerPage: t("component.table.rows_per_page", "Rows per page"),
                        sortByColumnAsc: t("component.table.sort_by.asc", "Sort by {column} ascending"),
                        sortByColumnDesc: t("component.table.sort_by.desc", "Sort by {column} descending"),
                        sortedByColumnAsc: t("component.table.sorted_by.asc", "Sorted by {column} ascending"),
                        sortedByColumnDesc: t("component.table.sorted_by.desc", "Sorted by {column} descending"),
                    }}
                    manualSorting={usesServerSideSorting}
                    enableSortingRemoval={true}
                    onSortingChange={sort.setSorting}
                    manualFiltering={usesServerSideFiltering}
                    columnFilterDisplayMode="popover"
                    onColumnFiltersChange={filters.setColumnFilters}
                />
            </div>
        </>
    );
};
